// Fatura + İrsaliye tam-sayfa detay ekranlarının paylaştığı "Yazdır + Ön İzleme" diyaloğu.
// Belge Dizaynı baskı motoru (printRenderer) + Seri Yönetimi (DocumentSeries) + ResolveData
// üzerine kurulur: kullanıcı bir seri seçer, serinin bağlı dizaynı (DocumentTemplate.LayoutJson)
// ile belgenin gerçek verisi (ResolveData) birleştirilip önizlenir / yazdırılır.
//
// Yazıcı seçimi: tarayıcı JS'i kurulu yazıcıları listeleyemez. Baskı, işletim sisteminin /
// tarayıcının kendi yazdırma diyaloğuyla yapılır (window.print → printLayout). Bu yüzden
// burada sahte bir yazıcı listesi sunulmaz; yalnızca kullanıcı bilgilendirilir.
import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders, getODataRows } from './listPageStyles';
import { buildPrintHtml, printLayout } from './belgeDizayni/printRenderer';

// API yanıt zarfını çöz (ApiResponse<T> → data/Data; yoksa ham gövde).
const unwrap = (json) => json?.data ?? json?.Data ?? json;

const muted = 'var(--text-secondary, #6b7280)';
const strong = 'var(--card-text-strong, #0b2046)';
const border = '1px solid var(--border-color, rgba(15,23,42,0.10))';

const seriesRowStyle = (active) => ({
  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
  border: active ? '1px solid var(--accent)' : border,
  borderRadius: 8, cursor: 'pointer', textAlign: 'left', width: '100%',
  background: active ? 'var(--accent-soft, rgba(0,115,234,0.10))' : 'var(--bg-card, #fff)',
  color: strong, marginBottom: 8,
});

const noteStyle = {
  display: 'flex', gap: 8, alignItems: 'flex-start',
  padding: '10px 12px', borderRadius: 8, fontSize: 12, lineHeight: 1.5,
  background: 'var(--accent-soft, rgba(0,115,234,0.08))', color: muted, marginTop: 12,
};

/**
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {number} docType     Fatura=2 / İrsaliye=1 (DocumentTemplate.DocType ile aynı)
 * @param {number} documentTip 0=Satış / 1=Alış / 2=İade / 3=Hizmet
 * @param {number} year        Belge yılı (eşleşen seri yoksa yıl filtresiz tekrar denenir)
 * @param {string} documentId  Yazdırılacak belgenin Id'si (ResolveData için)
 * @param {string} title       Baskı başlığı (örn. fatura kodu)
 */
export function DocumentPrintDialog({ open, onClose, docType, documentTip, year, documentId, title }) {
  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState([]);
  const [designNames, setDesignNames] = useState({}); // designId -> ad
  const [selectedSeriesId, setSelectedSeriesId] = useState('');
  const [yearFallback, setYearFallback] = useState(false); // yıl eşleşmedi → tüm seriler gösterildi
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false); // resolve sırasında butonları kilitle
  const [previewHtml, setPreviewHtml] = useState(null);

  // Dizayn adlarını topluca yükle (seri listesinde göstermek için).
  const loadDesignNames = useCallback(async (rows) => {
    const ids = [...new Set(rows.map((r) => r.designId).filter(Boolean))];
    if (!ids.length) return;
    try {
      const filter = ids.map((x) => `Id eq ${x}`).join(' or ');
      const url = `/api/documenttemplates?$filter=${encodeURIComponent(`(${filter})`)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) return;
      const map = {};
      getODataRows(await res.json()).forEach((d) => { map[d.id] = d.name || d.id; });
      setDesignNames(map);
    } catch { /* sessiz: ad gösterilemezse Id ile devam */ }
  }, []);

  // Açılınca serileri filtreli çek. Yıl eşleşmezse yıl filtresiz tekrar dene.
  const loadSeries = useCallback(async () => {
    setLoading(true); setError(''); setYearFallback(false);
    setSeries([]); setSelectedSeriesId('');
    const base = `DocumentType eq ${docType} and DocumentTip eq ${documentTip}`;
    const fetchSeries = async (filter) => {
      const url = `/api/documentseries?$filter=${encodeURIComponent(filter)}&$orderby=${encodeURIComponent('SeriesNo asc')}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Seriler alınamadı');
      return getODataRows(await res.json());
    };
    try {
      let rows = [];
      const hasYear = Number.isFinite(Number(year)) && Number(year) > 0;
      if (hasYear) {
        rows = await fetchSeries(`${base} and Year eq ${Number(year)}`);
        if (!rows.length) {
          const all = await fetchSeries(base);
          if (all.length) { setYearFallback(true); rows = all; }
        }
      } else {
        rows = await fetchSeries(base);
      }
      // Pasif serileri gizle.
      rows = rows.filter((r) => r.isActive !== false);
      setSeries(rows);
      if (rows.length === 1) setSelectedSeriesId(rows[0].id);
      loadDesignNames(rows);
    } catch (e) {
      setError(e.message || 'Seriler alınamadı');
    } finally {
      setLoading(false);
    }
  }, [docType, documentTip, year, loadDesignNames]);

  useEffect(() => {
    if (!open) return;
    setPreviewHtml(null);
    loadSeries();
  }, [open, loadSeries]);

  const selectedSeries = series.find((x) => x.id === selectedSeriesId) || null;

  // Seçili serinin dizaynını (LayoutJson) + belgenin verisini (ResolveData) çöz.
  const resolveLayoutAndData = async () => {
    if (!selectedSeries) { setError('Lütfen bir seri seçin'); return null; }
    if (!selectedSeries.designId) { setError('Bu seride dizayn seçili değil — Seri Yönetimi’nden dizayn atayın'); return null; }
    setError('');
    // Dizayn (DocumentTemplate) tek kayıt — GetById ile.
    const tplRes = await fetch(`/api/documenttemplates/GetById?id=${selectedSeries.designId}`, { headers: authHeaders() });
    if (!tplRes.ok) throw new Error('Belge dizaynı alınamadı');
    const tpl = unwrap(await tplRes.json());
    let layout;
    try { layout = JSON.parse(tpl?.layoutJson || tpl?.LayoutJson || 'null'); } catch { layout = null; }
    if (!layout || !layout.page) throw new Error('Bu dizaynın düzeni boş veya geçersiz');
    // Belge verisi — ResolveData (otoriter backend çözücü).
    const dataRes = await fetch(`/api/documenttemplates/ResolveData?docType=${docType}&documentId=${documentId}`, { headers: authHeaders() });
    if (!dataRes.ok) throw new Error('Belge verisi alınamadı');
    const data = unwrap(await dataRes.json());
    return { layout, data };
  };

  const handlePreview = async () => {
    setBusy(true);
    try {
      const r = await resolveLayoutAndData();
      if (r) setPreviewHtml(buildPrintHtml(r.layout, r.data, title || 'Belge'));
    } catch (e) {
      setError(e.message || 'Önizleme oluşturulamadı');
    } finally {
      setBusy(false);
    }
  };

  // Baskıda boş Matbu No'lu Fatura/İrsaliye'ye seri+yıl+sıra formatında otomatik numara
  // ata (talep #124 — örn. "HB1202600000001"). Belge zaten numaralıysa mevcut numara
  // korunur (yeniden baskıda DEĞİŞMEZ). Yalnızca 'Yazdır'da çağrılır, önizlemede çağrılmaz.
  const assignMatbuNo = async () => {
    if (docType !== 1 && docType !== 2) return; // yalnızca İrsaliye(1)/Fatura(2)
    if (!selectedSeries) return;
    const res = await fetch('/api/documentseries/AssignMatbuNo', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ seriesId: selectedSeries.id, docType, documentId }),
    });
    if (!res.ok) {
      let msg = 'Matbu No atanamadı';
      try { const j = await res.json(); msg = j?.message || j?.Message || msg; } catch { /* yut */ }
      throw new Error(msg);
    }
  };

  const handlePrint = async () => {
    setBusy(true);
    try {
      await assignMatbuNo();              // önce Matbu No ata (boşsa), sonra veriyi çöz
      const r = await resolveLayoutAndData();
      if (r) printLayout(r.layout, r.data, title || 'Belge');
    } catch (e) {
      setError(e.message || 'Yazdırma başlatılamadı');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: previewHtml ? 940 : 560 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={s.modalTitle}>{previewHtml ? 'Ön İzleme' : 'Yazdır / Ön İzleme'}{title ? ` — ${title}` : ''}</h3>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={s.modalBody}>
          {previewHtml ? (
            <iframe
              title="Belge Ön İzleme"
              srcDoc={previewHtml}
              style={{ width: '100%', height: '70vh', border, borderRadius: 8, background: '#fff' }}
            />
          ) : (
            <>
              <label style={{ display: 'block', color: muted, fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.2, marginBottom: 8 }}>
                Seri Seçimi
              </label>

              {loading && <div style={{ color: muted, padding: 12 }}>Seriler yükleniyor…</div>}

              {!loading && series.length === 0 && (
                <div style={{ color: muted, padding: 12, border, borderRadius: 8 }}>
                  Bu belge türü / tipi için tanımlı seri bulunamadı. Seri Yönetimi’nden bir seri ekleyin.
                </div>
              )}

              {!loading && yearFallback && (
                <div style={{ ...noteStyle, color: 'var(--warn, #b45309)', background: 'rgba(253,171,61,0.10)' }}>
                  <span>⚠️</span>
                  <span>{year} yılı için tanımlı seri yok — tüm seriler listeleniyor.</span>
                </div>
              )}

              {!loading && series.map((row) => {
                const active = row.id === selectedSeriesId;
                const designLabel = row.designId ? (designNames[row.designId] || 'Dizayn yükleniyor…') : 'Dizayn seçili değil';
                return (
                  <button
                    type="button"
                    key={row.id}
                    style={seriesRowStyle(active)}
                    onClick={() => { setSelectedSeriesId(row.id); setError(''); }}
                  >
                    <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, color: 'var(--accent)' }}>{row.seriesNo}</span>
                    <span style={{ color: muted, fontSize: 12 }}>· {row.year}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: row.designId ? muted : 'var(--danger)' }}>{designLabel}</span>
                  </button>
                );
              })}

              {selectedSeries && !selectedSeries.designId && (
                <div style={{ ...noteStyle, color: 'var(--danger)', background: 'rgba(226,68,92,0.08)' }}>
                  <span>⛔</span>
                  <span>Seçili seride dizayn atanmamış. Yazdırma/önizleme için Seri Yönetimi’nden bu seriye bir dizayn bağlayın.</span>
                </div>
              )}

              <div style={noteStyle}>
                <span>🖨️</span>
                <span>Yazıcı seçimi, yazdır’a bastığınızda açılan işletim sistemi / tarayıcı yazdırma penceresinde yapılır.</span>
              </div>

              {error && (
                <div style={{ ...noteStyle, color: 'var(--danger)', background: 'rgba(226,68,92,0.08)' }}>
                  <span>⚠️</span><span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div style={s.modalFooter}>
          {previewHtml ? (
            <>
              <button style={s.btnPrimary} disabled={busy} onClick={handlePrint}>Yazdır</button>
              <button style={s.btnSecondary} onClick={() => setPreviewHtml(null)}>Geri</button>
              <button style={s.btnSecondary} onClick={onClose}>Kapat</button>
            </>
          ) : (
            <>
              <button
                style={s.btnSecondary}
                disabled={busy || !selectedSeries || !selectedSeries.designId}
                onClick={handlePreview}
              >
                {busy ? 'Hazırlanıyor…' : 'Ön İzleme'}
              </button>
              <button
                style={s.btnPrimary}
                disabled={busy || !selectedSeries || !selectedSeries.designId}
                onClick={handlePrint}
              >
                {busy ? 'Hazırlanıyor…' : 'Yazdır'}
              </button>
              <button style={s.btnSecondary} onClick={onClose}>İptal</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentPrintDialog;
